import { Injectable, signal, computed } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  public readonly breadcrumbs;
  public readonly hasBreadcrumbs;
  private readonly breadcrumbsSignal = signal<MenuItem[]>([]);

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute
  ) {
    this.breadcrumbs = this.breadcrumbsSignal.asReadonly();
    this.hasBreadcrumbs = computed(() => this.breadcrumbs().length > 0);
    this.inicializarDeteccionAutomatica();
  }

  public setBreadcrumbs(breadcrumbs: MenuItem[]): void {
    this.breadcrumbsSignal.set(breadcrumbs);
  }

  public clearBreadcrumbs(): void {
    this.breadcrumbsSignal.set([]);
  }

  public addBreadcrumb(breadcrumb: MenuItem): void {
    const actual = this.breadcrumbsSignal();
    this.breadcrumbsSignal.set([...actual, breadcrumb]);
  }

  public actualizarUltimoBreadcrumb(breadcrumb: MenuItem): void {
    const actual = this.breadcrumbsSignal();
    if (actual.length > 0) {
      const actualizado = [...actual.slice(0, -1), breadcrumb];
      this.breadcrumbsSignal.set(actualizado);
    }
  }

  private inicializarDeteccionAutomatica(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const breadcrumbs = this.crearBreadcrumbsDesdeRuta(this.activatedRoute.root);
        const ultimo = breadcrumbs.at(-1);
        if (ultimo) {
          ultimo.routerLink = undefined;
        }
        this.breadcrumbsSignal.set(breadcrumbs);
      });
  }

  private crearBreadcrumbsDesdeRuta(
    route: ActivatedRoute | null,
    url: string = '',
    breadcrumbs: MenuItem[] = []
  ): MenuItem[] {
    if (!route) {
      return breadcrumbs;
    }

    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = (child.snapshot.url || []).map(segment => segment.path).join('/');
      
      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      const breadcrumbData = child.snapshot.data['breadcrumb'];
      const parentBreadcrumb = child.snapshot.data['parentBreadcrumb'];
      const icon = child.snapshot.data['breadcrumbIcon'];
      
      this.updateParentBreadcrumb(parentBreadcrumb, breadcrumbs);
      
      const { label, breadcrumbIcon } = this.extraerInfoBreadcrumb(breadcrumbData, icon);
      
      if (label && this.debeAgregarBreadcrumb(label, routeURL)) {
        const item = this.crearItemBreadcrumb(label, breadcrumbIcon, url);
        breadcrumbs.push(item);
      }

      breadcrumbs = this.crearBreadcrumbsDesdeRuta(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }

  private updateParentBreadcrumb(parentBreadcrumb: unknown, breadcrumbs: MenuItem[]): void {
    if (!parentBreadcrumb || breadcrumbs.length === 0) {
      return;
    }

    const ultimoBreadcrumb = breadcrumbs.at(-1);
    if (!ultimoBreadcrumb) {
      return;
    }

    if (typeof parentBreadcrumb === 'string') {
      ultimoBreadcrumb.label = parentBreadcrumb;
    } else if (typeof parentBreadcrumb === 'object') {
      const parentObj = parentBreadcrumb as Record<string, unknown>;
      ultimoBreadcrumb.label = (parentObj['label'] as string) || ultimoBreadcrumb.label;
      ultimoBreadcrumb.icon = ((parentObj['info'] as Record<string, unknown>)?.['icon'] as string) || ultimoBreadcrumb.icon;
    }
  }

  private extraerInfoBreadcrumb(
    breadcrumbData: unknown,
    icon: string
  ): { label: string | undefined; breadcrumbIcon: string } {
    let label: string | undefined;
    let breadcrumbIcon = icon;

    if (typeof breadcrumbData === 'string') {
      label = breadcrumbData;
    } else if (breadcrumbData && typeof breadcrumbData === 'object') {
      const dataObj = breadcrumbData as Record<string, unknown>;
      if (dataObj['skip'] === true) {
        label = undefined;
      } else {
        label = dataObj['label'] as string;
        breadcrumbIcon = ((dataObj['info'] as Record<string, unknown>)?.['icon'] as string) || breadcrumbIcon;
      }
    }

    return { label, breadcrumbIcon };
  }

  private debeAgregarBreadcrumb(label: string | undefined, routeURL: string): boolean {
    return !!label && routeURL !== '';
  }

  private crearItemBreadcrumb(
    label: string,
    breadcrumbIcon: string,
    url: string
  ): MenuItem {
    const item: MenuItem = {
      label: label,
      icon: breadcrumbIcon || '',
      routerLink: url || '/'
    };

    return item;
  }
}
